import { useState, useEffect } from "react";
import Link from "next/link";
import { useRouter } from "next/router";

import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import ArrowBackOutlinedIcon from "@mui/icons-material/ArrowBackOutlined";

const DrawerStyled = styled(Drawer)(({ theme }) => ({
  flexShrink: 1,
  opacity: "0",
  pointerEvents: "none",
  position: "relative",
  transition: "all 0.3s",
  width: theme.spacing(23),
  zIndex: "99",

  "&.display": {
    opacity: "1",
    pointerEvents: "auto",
  },

  [theme.breakpoints.down("md")]: {
    width: theme.spacing(0),
  },

  "& .MuiDrawer-paper": {
    backgroundImage: theme.background.linearGradient,
    border: "none",
    borderRadius: `0 ${theme.shape.borderRadius * 8}px ${
      theme.shape.borderRadius * 8
    }px 0`,
    boxSizing: "border-box",
    boxShadow: theme.customShadows.sideBarShadow1,
    height: "auto",
    opacity: "0.85",
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(4),
    transform: "translate(0%, 105px)",
    width: theme.spacing(43),

    "&:hover": {
      opacity: "0.95",
    },

    [theme.breakpoints.down("md")]: {
      opacity: "0.95",
    },
  },
}));

const ItemContainer = styled("div")(({ theme }) => ({
  alignItems: "center",
  display: "flex",
  opacity: "0.6",

  "&:hover": {
    opacity: "1",
  },

  "&.active": {
    borderLeft: `2px solid ${theme.palette.secondary.main}`,
    opacity: "1",
  },

  "& svg": {
    opacity: "0.6",
  },

  "&.active svg, &:hover svg": {
    opacity: "1",
    transform: "translateX(3px)",
  },
}));

const ListItemTextStyled = styled(ListItemText)(({ theme }) => ({
  color: theme.palette.common.white,
  paddingLeft: theme.spacing(1.5),

  "&.active span": {
    fontWeight: "bold",
  },
}));

const CloseIconContainer = styled("div")(({ theme }) => ({
  color: theme.palette.common.white,
  cursor: "pointer",
  height: theme.spacing(7),
  position: "absolute",
  right: theme.spacing(3),
  top: theme.spacing(3),
  transition: "all 0.3s",
  width: theme.spacing(7),

  "& svg": {
    height: "100%",
    width: "100%",
  },

  "& svg:hover": {
    color: theme.palette.secondary.light,
    transform: "translateX(-3px)",
  },
}));

const MenuIconContainer = styled("aside")(({ theme }) => ({
  backgroundImage: theme.background.linearGradient,
  borderRadius: `0 ${theme.shape.borderRadius * 4}px ${
    theme.shape.borderRadius * 4
  }px 0`,
  boxShadow: theme.customShadows.sideBarShadow1,
  color: theme.palette.common.white,
  cursor: "pointer",
  left: "-1px",
  padding: theme.spacing(3),
  opacity: "0",
  pointerEvents: "none",
  position: "fixed",
  top: "150px",
  transition: "all 0.3s",
  width: theme.spacing(15),
  zIndex: "99",

  "&:hover": {
    backgroundImage: theme.background.reverseLinearGradient,
    width: theme.spacing(16),
  },
  "&:hover svg": {
    transform: "translateX(3px)",
  },
  "&.display": {
    opacity: "1",
    pointerEvents: "auto",
  },
}));

const Sidebar = (props) => {
  const { setCurrentPage } = props;
  const [isDisplaySidebar, setIsDisplaySidebar] = useState(true);

  const theme = useTheme();
  const router = useRouter();
  const matchesSM = useMediaQuery(theme.breakpoints.down("md"));

  const { pathname, query } = router;

  useEffect(() => {
    if (matchesSM && (query.slug || pathname)) {
      setIsDisplaySidebar(false);
    }

    if (query.slug) {
      setCurrentPage(1);
    }
  }, [matchesSM, query, pathname, setCurrentPage]);

  const handleToggleSidebar = () => {
    setIsDisplaySidebar(!isDisplaySidebar);
  };

  return (
    <>
      <DrawerStyled
        variant="permanent"
        anchor="left"
        className={isDisplaySidebar ? "display" : ""}
      >
        <CloseIconContainer onClick={handleToggleSidebar}>
          <ArrowBackOutlinedIcon />
        </CloseIconContainer>
        <List>
          {props.content.map((item) => (
            <Link
              key={item.slug}
              href={props.type ? `/${props.type}/${item.slug}` : `${item.slug}`}
              passHref
            >
              <ListItem component="a" sx={{ maxHeight: theme.spacing(15) }}>
                <ItemContainer
                  className={
                    (item.slug === query.slug || item.slug === pathname) &&
                    "active"
                  }
                >
                  <ListItemTextStyled
                    primary={item.title}
                    className={
                      (item.slug === query.slug || item.slug === pathname) &&
                      "active"
                    }
                  />
                  <KeyboardArrowRightIcon
                    sx={{ color: theme.palette.secondary.main }}
                  />
                </ItemContainer>
              </ListItem>
            </Link>
          ))}
        </List>
      </DrawerStyled>
      <MenuIconContainer
        onClick={handleToggleSidebar}
        className={!isDisplaySidebar ? "display" : ""}
      >
        <ArrowForwardOutlinedIcon />
        <Typography>
          {props.type === "categories" ? "Thể loại" : "Tài khoản"}
        </Typography>
      </MenuIconContainer>
    </>
  );
};

export default Sidebar;
